import SendIcon from "@mui/icons-material/SendRounded";
import { Button, ListItem, Stack, TextField, Typography } from "@mui/material";
import deepEqual from "fast-deep-equal";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import AutocompleteCreatable from "@entrilia/shared/components/AutocompleteCreatable";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import {
  combineValidators,
  emailValidator,
  requiredValidator,
  validResult,
} from "@entrilia/shared/utilities/validators";
import adminApi from "../../../../../api/adminApi";
import type { Fundraising, TestEmailContact } from "../../../../../api/types/fundraisingTypes";
import useUserContext from "../../../../../context/useUserContext";
import type { EmailTemplateSettings } from "../../../../common/email-templates/EmailTemplateEditor";
import EmailTemplateEditor from "../../../../common/email-templates/EmailTemplateEditor";
import useFundraisingDetailsPageContext from "./useFundraisingDetailsPageContext";

const updateFundraising = withErrorHandling(adminApi.updateFundraising);
const sendTestFundraisingEmail = withErrorHandling(adminApi.sendTestFundraisingEmail);

const snippets = [{ value: "{{RecipientName}}", displayName: "Recipient Name" }];

const defaultSubject = "You've been invited to access the Fundraising Data Room";

const getInitialSettings = (fundraising: Fundraising) => ({
  subject: fundraising.notificationEmailSettings?.subject ?? defaultSubject,
  body: fundraising.notificationEmailSettings?.body ?? "",
  cc: fundraising.notificationEmailSettings?.recipientsCC ?? "",
  bcc: fundraising.notificationEmailSettings?.recipientsBCC ?? "",
});

const validateEmail = combineValidators(requiredValidator, emailValidator);

const splitCommaSeparatedString = (str: string) =>
  str
    .split(",")
    .map((s) => sanitizeEmail(s))
    .filter(Boolean);

const validateCommaSeparatedEmailString = (str: string) => {
  const emails = splitCommaSeparatedString(str);
  for (const email of emails) {
    const result = emailValidator(email);
    if (!result.isValid) {
      return result;
    }
  }

  return validResult();
};

const validateSettings = (settings: EmailTemplateSettings) => {
  return {
    subject: requiredValidator(settings.subject),
    body: requiredValidator(settings.body),
    cc: validateCommaSeparatedEmailString(settings.cc),
    bcc: validateCommaSeparatedEmailString(settings.bcc),
  };
};

const FundraisingNotificationsConfig = () => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { isContentEditable, hasEditPermissions, fundraising, onUpdated } = useFundraisingDetailsPageContext();
  const { email: currentUserEmail } = useUserContext();

  const getTestEmailContactsForFundraising = useCallback(
    () => adminApi.getTestEmailContactsForFundraising(fundraising.id),
    [fundraising.id]
  );

  const [testInvitationContactsResp, fetchTestContactsError, { isFetching: isFetchingTestContacts }] = useFetch(
    getTestEmailContactsForFundraising,
    { disabled: !hasEditPermissions }
  );

  const [currentSettings, setCurrentSettings] = useState<EmailTemplateSettings>(() => getInitialSettings(fundraising));
  const [isSaving, setIsSaving] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState(currentUserEmail);
  const [selectedContact, setSelectedContact] = useState<TestEmailContact>();
  const [isSending, setIsSending] = useState(false);

  if (hasEditPermissions && fetchTestContactsError) {
    logError(fetchTestContactsError, "[FundraisingNotificationsConfig] getTestEmailContactsForFundraising");
    return <DataLoadingFailed title="Failed to load test email contacts" />;
  }

  if (hasEditPermissions && (isFetchingTestContacts || testInvitationContactsResp === undefined)) {
    return <InlineLoader />;
  }

  const handleSettingsChange = (update: Partial<EmailTemplateSettings>) =>
    setCurrentSettings((prev) => ({
      ...prev,
      ...update,
    }));

  const handleSave = async () => {
    setIsSaving(true);

    const [updatedFundraising, error] = await updateFundraising(fundraising.id, {
      expectedEtag: fundraising.etag,
      notificationEmailSettings: {
        subject: currentSettings.subject.trim(),
        body: currentSettings.body.trim(),
        recipientsCC: splitCommaSeparatedString(currentSettings.cc).join(","),
        recipientsBCC: splitCommaSeparatedString(currentSettings.bcc).join(","),
      },
    });

    setIsSaving(false);

    if (error) {
      logError(error, "[FundraisingNotificationsConfig]");
      sendNotificationError(error.message);
      return;
    }

    onUpdated(updatedFundraising);
    sendNotification("Notification settings updated successfully");
    setCurrentSettings(getInitialSettings(updatedFundraising));
  };

  const handleReset = () => setCurrentSettings(getInitialSettings(fundraising));

  const handleSendTestEmailButtonClick = async () => {
    if (!selectedContact) {
      return;
    }

    setIsSending(true);

    const sanitizedEmail = sanitizeEmail(testEmailAddress);
    const [, error] = await sendTestFundraisingEmail(fundraising.id, {
      recipientEmail: sanitizedEmail,
      contactId: selectedContact.id,
    });

    setIsSending(false);

    if (error) {
      logError(error, "[SendTestInvitationEmail]");
      sendNotificationError(error.message);
      return;
    }

    sendNotification(`Test invitation email sent to ${sanitizedEmail}`);
  };

  const isDirty = !deepEqual(getInitialSettings(fundraising), currentSettings);

  const settingsValidationResults = validateSettings(currentSettings);

  const areSettingsValid = Object.values(settingsValidationResults).every((result) => result.isValid);

  const testEmailValidationResult = validateEmail(sanitizeEmail(testEmailAddress));

  const testEmailContacts = testInvitationContactsResp?.contacts ?? [];

  return (
    <Stack spacing={2.5} sx={{ flex: 1, py: 2.5 }}>
      <Stack spacing={0.5} sx={{ pt: 2 }}>
        <Typography variant="subtitle1">Notifications</Typography>
        <Typography variant="caption" color="textSecondary">
          Send notification emails when the fundraising goes live.
        </Typography>
      </Stack>
      <Stack spacing={2} sx={{ pt: 2 }}>
        <Typography variant="subtitle1">Email</Typography>

        <EmailTemplateEditor
          snippets={snippets}
          settings={currentSettings}
          onChange={handleSettingsChange}
          disabled={!isContentEditable}
          validationResults={isDirty ? settingsValidationResults : {}}
        />

        <Stack direction="row" spacing={1} sx={{ pt: 1.5 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={!isDirty || !areSettingsValid || !isContentEditable}
            loading={isSaving}
            onClick={handleSave}
          >
            Save
          </Button>
          <Button variant="text" color="secondary" disabled={!isDirty || !isContentEditable} onClick={handleReset}>
            Cancel
          </Button>
        </Stack>
      </Stack>
      {testEmailContacts.length > 0 && (
        <Stack spacing={2} sx={{ width: "100%" }}>
          <Typography variant="subtitle1" sx={{ pt: 2 }}>
            Test Email
          </Typography>

          <Stack direction="row" spacing={1}>
            <TextField
              fullWidth
              variant="outlined"
              label="Email"
              value={testEmailAddress}
              onChange={({ target }) => setTestEmailAddress(target.value)}
            />

            <AutocompleteCreatable
              withLazyRendering
              noItemsText="No contacts found"
              openOnFocus
              fullWidth
              options={testEmailContacts}
              getOptionLabel={(o) => `${o.fullName} <${o.email}>`}
              renderOption={({ key: _key, ...props }, option) => (
                <ListItem key={option.id} {...props}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2">{option.fullName}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.email}
                    </Typography>
                  </Stack>
                </ListItem>
              )}
              isOptionEqualToValue={(opt, val) => opt.email === val.email}
              renderInput={(params) => <TextField {...params} label="Preview notification as" />}
              value={selectedContact ?? null}
              onChange={(_, newValue) => {
                setSelectedContact(newValue ?? undefined);
              }}
            />
          </Stack>

          <Button
            variant="outlined"
            color="primary"
            loading={isSending}
            endIcon={<SendIcon />}
            disabled={isDirty || !areSettingsValid || !testEmailValidationResult.isValid || !selectedContact}
            onClick={handleSendTestEmailButtonClick}
            sx={{ width: "7.5rem" }}
          >
            Send Email
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default FundraisingNotificationsConfig;
